import * as Sentry from "@sentry/react";
import type { ReactNode } from "react";
import { Component } from "react";

interface State {
  hasError: boolean;
  error?: Error;
}

const CHUNK_RELOAD_KEY = "eb_chunk_reloaded";

function isChunkError(error: Error): boolean {
  return (
    error.name === "ChunkLoadError" ||
    /Failed to fetch dynamically imported module|Importing a module script failed/i.test(error.message)
  );
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidMount() {
    // Clear the reload flag after a successful render so future chunk errors can reload again.
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[ErrorBoundary]", error);
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });

    // PWA chunk mismatch after SW update: reload once to pick up fresh assets.
    if (isChunkError(error) && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      const { error } = this.state;
      return (
        <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-8 text-center">
          <h1 className="text-xl font-bold text-gray-900">Qualcosa è andato storto</h1>
          <p className="text-sm text-gray-500">Riprova o contatta un amministratore</p>
          {error && (
            <p className="text-xs text-gray-400 font-mono bg-gray-100 rounded-lg px-3 py-2 max-w-xs break-all">
              {error.name}: {error.message}
            </p>
          )}
          <button
            onClick={() => this.setState({ hasError: false })}
            className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Riprova
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
